// =============================================================================
// AllocationSidebar.jsx - Apple-Style Navigation Sidebar Component 
// =============================================================================
// Desktop and mobile navigation sidebar for pitch allocator
// - Responsive pitch selector: popout menu on smaller screens (< 1920px)
// - Club information display with user profile and role badge
// - Collapsible facility groups with nested pitch navigation
// - Allocation counts per pitch and aggregated per facility
// - Quick links (Club Map, Reporting, Settings)
// - Feedback and logout buttons
// - Mobile hamburger menu variant
// =============================================================================

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { AdminOnly } from '../../contexts/UserContext';
import { Hint } from './UnifiedPitchAllocator';

/**
 * Custom hook to track window width for responsive behavior
 */
const useWindowWidth = () => {
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1920
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowWidth;
};

/**
 * AllocationSidebar Component - Apple-Style Design
 */
const AllocationSidebar = ({
  clubInfo,
  user,
  userRole,
  isAdmin,
  facilities = [],
  pitches,
  pitchNames,
  currentPitchId,
  selectedFacilityId,
  allPitchAllocations,
  allocations,
  onNavigateToPitch,
  onNavigateToMenu,
  onNavigateToClubMap,
  onNavigateToReporting,
  onNavigateToSettings,
  onFeedback,
  onLogout,
  logoSrc,
  branding,
  isMobile,
  isMobileOpen,
  onMobileClose,
  hintRefresh,
  isTrialExpired = false
}) => {
  
  const windowWidth = useWindowWidth();
  const useCompactMode = windowWidth < 1920;
  
  // State for popout pitch selector
  const [isPitchSelectorOpen, setIsPitchSelectorOpen] = useState(false);
  const pitchSelectorRef = useRef(null);
  
  // Track which facilities are expanded (default: all expanded, or just the selected one)
  const [expandedFacilities, setExpandedFacilities] = useState(() => {
    if (selectedFacilityId) {
      return { [selectedFacilityId]: true };
    }
    const initial = {};
    facilities.forEach(f => {
      initial[f.id] = true;
    });
    return initial;
  });

  // Close popout when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pitchSelectorRef.current && !pitchSelectorRef.current.contains(event.target)) {
        setIsPitchSelectorOpen(false);
      }
    };
    
    if (isPitchSelectorOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isPitchSelectorOpen]);

  // Close popout on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsPitchSelectorOpen(false);
      }
    };
    
    if (isPitchSelectorOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isPitchSelectorOpen]);

  // Normalize pitch ID to standard format (pitch-1, pitch-2, etc.)
  const normalizePitchId = (pitchId) => {
    if (!pitchId) return null;
    const match = pitchId.toString().match(/(\d+)/);
    if (match) {
      return `pitch-${match[1]}`;
    }
    return pitchId;
  };

  // Get allocation count for a specific pitch on the current date
  const getAllocationsCountForPitch = (pitchId) => {
    const normalizedId = normalizePitchId(pitchId);
    if (!normalizedId) return 0;
    
    let count = 0;
    Object.keys(allPitchAllocations || {}).forEach(key => {
      if (key.includes(`-${normalizedId}-`)) {
        count++;
      }
    });
    return count;
  };

  // Get total allocation count for a facility
  const getAllocationsCountForFacility = (facility) => {
    if (!facility?.pitches) return 0;
    return facility.pitches.reduce((total, pitch) => {
      const pitchId = pitch.id || `pitch-${pitch.pitchNumber}`;
      return total + getAllocationsCountForPitch(pitchId);
    }, 0);
  };

  // Toggle facility expansion
  const toggleFacility = (facilityId) => {
    setExpandedFacilities(prev => ({
      ...prev,
      [facilityId]: !prev[facilityId]
    }));
  };

  // Build facility-grouped structure from either facilities prop or legacy pitches prop
  const facilityGroups = useMemo(() => {
    if (facilities && facilities.length > 0) {
      return facilities.map(facility => ({
        id: facility.id,
        name: facility.name || 'Unnamed Facility',
        isPrimary: facility.isPrimary || false,
        pitches: (facility.pitches || []).map((pitch, idx) => ({
          id: pitch.id || `pitch-${pitch.pitchNumber}`,
          pitchNumber: pitch.pitchNumber || (idx + 1),
          name: pitch.name,
          facilityId: facility.id
        }))
      }));
    }
    
    if (pitches && pitches.length > 0) {
      return [{
        id: 'default',
        name: clubInfo?.name || 'Main Facility',
        isPrimary: true,
        pitches: pitches.map((pitch, idx) => ({
          id: `pitch-${pitch.pitchNumber || (idx + 1)}`,
          pitchNumber: pitch.pitchNumber || (idx + 1),
          name: pitch.name,
          facilityId: 'default'
        }))
      }];
    }
    
    return [];
  }, [facilities, pitches, clubInfo?.name]);

  // Get total pitch count
  const totalPitchCount = useMemo(() => {
    return facilityGroups.reduce((sum, f) => sum + f.pitches.length, 0);
  }, [facilityGroups]);

  // Get display name for a pitch
  const getPitchDisplayName = (pitch) => {
    const normalizedId = pitch.id || `pitch-${pitch.pitchNumber}`;
    
    if (pitchNames && pitchNames[normalizedId]) {
      const customName = pitchNames[normalizedId];
      if (customName && customName.trim() !== '') {
        return customName;
      }
    }
    
    if (pitch.name && pitch.name.trim() !== '') {
      return pitch.name;
    }
    
    return `Pitch ${pitch.pitchNumber}`;
  };

  // Get current pitch info for compact selector
  const currentPitchInfo = useMemo(() => {
    for (const facility of facilityGroups) {
      for (const pitch of facility.pitches) {
        const normalizedId = pitch.id || `pitch-${pitch.pitchNumber}`;
        if (currentPitchId === normalizedId) {
          return {
            pitch,
            facility,
            displayName: getPitchDisplayName(pitch),
            allocCount: getAllocationsCountForPitch(normalizedId)
          };
        }
      }
    }
    return null;
  }, [facilityGroups, currentPitchId, pitchNames, allPitchAllocations]);

  // Apple-style disclosure triangle component
  const DisclosureTriangle = ({ isExpanded, onClick }) => (
    <button
      onClick={onClick}
      style={{
        background: 'none',
        border: 'none',
        padding: '4px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'transform 0.2s ease',
        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
        color: 'rgba(255,255,255,0.5)',
        fontSize: '8px',
        width: '16px',
        height: '16px'
      }}
      aria-label={isExpanded ? 'Collapse' : 'Expand'}
    >
      ▶
    </button>
  );

  // Render a single pitch row
  const PitchRow = ({ pitch, isSelected, inPopout = false }) => {
    const allocCount = getAllocationsCountForPitch(pitch.id);
    const displayName = getPitchDisplayName(pitch);
    
    return (
      <button
        onClick={() => {
          onNavigateToPitch(pitch.pitchNumber, pitch.facilityId);
          if (isMobile && onMobileClose) {
            onMobileClose();
          }
          if (inPopout) {
            setIsPitchSelectorOpen(false);
          }
        }}
        style={{
          width: '100%',
          padding: inPopout ? '8px 10px 8px 24px' : '8px 12px 8px 36px',
          backgroundColor: isSelected 
            ? 'rgba(59, 130, 246, 0.3)'
            : 'transparent',
          border: 'none',
          borderRadius: '6px',
          fontSize: inPopout ? '12px' : '13px',
          fontWeight: isSelected ? '600' : '400',
          color: isSelected ? 'white' : 'rgba(255,255,255,0.85)',
          cursor: 'pointer',
          textAlign: 'left',
          transition: 'all 0.15s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px'
        }}
        onMouseEnter={(e) => {
          if (!isSelected) {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected) {
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
      >
        <span style={{ 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          {isSelected && (
            <span style={{ color: '#60a5fa', fontSize: '12px' }}>✓</span>
          )}
          {displayName}
        </span>
        {allocCount > 0 && (
          <span style={{
            fontSize: '10px',
            color: isSelected ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.5)',
            backgroundColor: isSelected 
              ? 'rgba(255,255,255,0.2)' 
              : 'rgba(255,255,255,0.1)',
            padding: '2px 5px',
            borderRadius: '10px',
            fontWeight: '500',
            minWidth: '18px',
            textAlign: 'center'
          }}>
            {allocCount}
          </span>
        )}
      </button>
    );
  };

  // Render a facility group header
  const FacilityHeader = ({ facility, isExpanded, totalAllocations, inPopout = false }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: inPopout ? '5px 6px' : '6px 8px',
        marginBottom: '2px',
        borderRadius: '6px',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease'
      }}
      onClick={() => toggleFacility(facility.id)}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      <DisclosureTriangle 
        isExpanded={isExpanded} 
        onClick={(e) => {
          e.stopPropagation();
          toggleFacility(facility.id);
        }}
      />
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        alignItems: 'center', 
        gap: '6px',
        overflow: 'hidden'
      }}>
        <span style={{
          fontSize: inPopout ? '10px' : '12px',
          fontWeight: '600',
          color: 'rgba(255,255,255,0.6)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {facility.name}
        </span>
        {facility.isPrimary && (
          <span style={{
            fontSize: '8px',
            color: '#10b981',
            backgroundColor: 'rgba(16, 185, 129, 0.15)',
            padding: '2px 4px',
            borderRadius: '3px',
            fontWeight: '600',
            flexShrink: 0
          }}>
            PRIMARY
          </span>
        )}
      </div>
      {totalAllocations > 0 && (
        <span style={{
          fontSize: '10px',
          color: 'rgba(255,255,255,0.5)',
          backgroundColor: 'rgba(255,255,255,0.1)',
          padding: '2px 5px',
          borderRadius: '10px',
          fontWeight: '500',
          marginLeft: '6px'
        }}>
          {totalAllocations}
        </span>
      )}
    </div>
  );

  // Render all facility groups with their pitches
  const renderFacilityGroups = (inPopout = false) => {
    if (facilityGroups.length === 0) {
      return (
        <div style={{
          padding: '12px',
          backgroundColor: 'rgba(255,255,255,0.05)',
          borderRadius: '8px',
          fontSize: '12px',
          color: 'rgba(255,255,255,0.6)',
          textAlign: 'center'
        }}>
          No facilities configured
        </div>
      );
    }

    return facilityGroups.map((facility, facilityIndex) => {
      const isExpanded = expandedFacilities[facility.id] !== false;
      const totalAllocations = getAllocationsCountForFacility({ pitches: facility.pitches });
      
      return (
        <div 
          key={facility.id} 
          style={{ 
            marginBottom: facilityIndex < facilityGroups.length - 1 ? '8px' : 0 
          }}
        >
          <FacilityHeader 
            facility={facility}
            isExpanded={isExpanded}
            totalAllocations={totalAllocations}
            inPopout={inPopout}
          />
          
          <div style={{
            overflow: 'hidden',
            maxHeight: isExpanded ? `${facility.pitches.length * (inPopout ? 36 : 44)}px` : '0px',
            opacity: isExpanded ? 1 : 0,
            transition: 'all 0.25s ease-out',
            marginLeft: inPopout ? '2px' : '4px'
          }}>
            {facility.pitches.map((pitch) => {
              const normalizedId = pitch.id || `pitch-${pitch.pitchNumber}`;
              const isSelected = currentPitchId === normalizedId && 
                                 (selectedFacilityId === facility.id || !selectedFacilityId);
              
              return (
                <PitchRow 
                  key={`${facility.id}-${pitch.id}`}
                  pitch={pitch}
                  isSelected={isSelected}
                  inPopout={inPopout}
                />
              );
            })}
          </div>
        </div>
      );
    });
  };

  // ============================================================================
  // APPLE-STYLE COMPACT PITCH SELECTOR (for screens < 1920px)
  // ============================================================================
  const CompactPitchSelector = () => (
    <div 
      ref={pitchSelectorRef}
      style={{ 
        position: 'relative',
        padding: '12px 8px',
        flex: 1 // Take remaining space to push bottom nav down
      }}
    >
      <Hint 
        hintKey="hint_select_pitch" 
        message="Select which pitch you want to allocate." 
        position="right"
        refresh={hintRefresh}
      />
      
      {/* Section Header */}
      <div style={{
        fontSize: '11px',
        fontWeight: '600',
        color: 'rgba(255,255,255,0.5)',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        padding: '8px 12px 8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <span>Facilities & Pitches</span>
        <span style={{
          fontSize: '10px',
          color: 'rgba(255,255,255,0.4)',
          fontWeight: '400',
          textTransform: 'none',
          letterSpacing: 'normal'
        }}>
          {totalPitchCount} {totalPitchCount === 1 ? 'pitch' : 'pitches'}
        </span>
      </div>
      
      {/* Compact Selector Button */}
      <button
        onClick={() => setIsPitchSelectorOpen(!isPitchSelectorOpen)}
        style={{
          width: '100%',
          padding: '12px 14px',
          backgroundColor: isPitchSelectorOpen ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.1)',
          border: isPitchSelectorOpen ? '1px solid rgba(255,255,255,0.3)' : '1px solid rgba(255,255,255,0.15)',
          borderRadius: '10px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
          transition: 'all 0.2s ease',
          marginTop: '4px'
        }}
        onMouseEnter={(e) => {
          if (!isPitchSelectorOpen) {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isPitchSelectorOpen) {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)';
          }
        }}
      >
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'flex-start',
          gap: '2px',
          overflow: 'hidden',
          flex: 1
        }}>
          {currentPitchInfo ? (
            <>
              <span style={{
                fontSize: '14px',
                fontWeight: '600',
                color: 'white',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
                textAlign: 'left'
              }}>
                {currentPitchInfo.displayName}
              </span>
              <span style={{
                fontSize: '11px',
                color: 'rgba(255,255,255,0.6)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
                textAlign: 'left'
              }}>
                {currentPitchInfo.facility.name}
                {currentPitchInfo.allocCount > 0 && ` • ${currentPitchInfo.allocCount} allocations`}
              </span>
            </>
          ) : (
            <span style={{
              fontSize: '14px',
              fontWeight: '500',
              color: 'rgba(255,255,255,0.7)'
            }}>
              Select a pitch...
            </span>
          )}
        </div>
        
        {/* Chevron */}
        <span style={{
          fontSize: '10px',
          color: 'rgba(255,255,255,0.5)',
          transition: 'transform 0.2s ease',
          transform: isPitchSelectorOpen ? 'rotate(-90deg)' : 'rotate(90deg)'
        }}>
          ▶
        </span>
      </button>
      
      {/* Popout Menu - Positioned to the RIGHT of sidebar */}
      {isPitchSelectorOpen && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '250px', // Right edge of sidebar
            transform: 'translateY(-50%)',
            marginLeft: '8px',
            backgroundColor: '#1e3a5f', // Pitchallo navy blue
            borderRadius: '12px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.4), 0 4px 20px rgba(0,0,0,0.2)',
            zIndex: 99999, // Always on top
            width: '220px',
            animation: 'slideRight 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
            border: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <style>{`
            @keyframes slideRight {
              from { 
                opacity: 0; 
                transform: translateY(-50%) translateX(-12px); 
              }
              to { 
                opacity: 1; 
                transform: translateY(-50%) translateX(0); 
              }
            }
          `}</style>
          
          {/* Popout Header */}
          <div style={{
            padding: '12px 14px 10px',
            borderBottom: '1px solid rgba(255,255,255,0.1)'
          }}>
            <div style={{
              fontSize: '13px',
              fontWeight: '600',
              color: 'white'
            }}>
              Select Pitch
            </div>
            <div style={{
              fontSize: '11px',
              color: 'rgba(255,255,255,0.5)',
              marginTop: '2px'
            }}>
              {facilityGroups.length} {facilityGroups.length === 1 ? 'facility' : 'facilities'} • {totalPitchCount} {totalPitchCount === 1 ? 'pitch' : 'pitches'}
            </div>
          </div>
          
          {/* Pitch List - No scroll, shows all */}
          <div style={{ padding: '6px' }}>
            {renderFacilityGroups(true)}
          </div>
        </div>
      )}
    </div>
  );

  // ============================================================================
  // FULL PITCH NAVIGATION (for screens >= 1920px)
  // ============================================================================
  const FullPitchNavigation = () => (
    <div style={{
      flex: 1,
      overflowY: 'auto',
      padding: '12px 8px'
    }}>
      <Hint 
        hintKey="hint_select_pitch" 
        message="Select which pitch you want to allocate from the sidebar." 
        position="right"
        refresh={hintRefresh}
      />
      
      {/* Section Header */}
      <div style={{
        fontSize: '11px',
        fontWeight: '600',
        color: 'rgba(255,255,255,0.5)',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        padding: '8px 12px 12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <span>Facilities & Pitches</span>
        <span style={{
          fontSize: '10px',
          color: 'rgba(255,255,255,0.4)',
          fontWeight: '400',
          textTransform: 'none',
          letterSpacing: 'normal'
        }}>
          {facilityGroups.length} {facilityGroups.length === 1 ? 'facility' : 'facilities'}
        </span>
      </div>
      
      {/* Facility Groups */}
      <div style={{
        display: 'flex',
        flexDirection: 'column'
      }}>
        {renderFacilityGroups(false)}
      </div>
    </div>
  );

  // Sidebar content component (used for both desktop and mobile)
  const SidebarContent = () => {
    return (
    <>
      {/* Logo and Club Info Header */}
      <div style={{
        padding: '12px',
        flexShrink: 0
      }}>
        {/* Pitchallo Logo - Outside branded card */}
        {logoSrc && (
          <div style={{
            marginBottom: '12px',
            textAlign: 'center'
          }}>
            <img 
              src={logoSrc} 
              alt="Pitchallo" 
              style={{
                height: '40px',
                width: 'auto',
                objectFit: 'contain'
              }}
            />
          </div>
        )}
        
        {/* Branded Club Info Card */}
        <div style={{
          background: `linear-gradient(135deg, ${branding?.primaryColor || '#10b981'} 0%, ${branding?.secondaryColor || '#3b82f6'} 100%)`,
          borderRadius: '12px',
          padding: '16px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
        }}>
        
        {/* Club Logo + Name Row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          marginBottom: '6px'
        }}>
          {branding?.clubLogoUrl && (
            <img 
              src={branding.clubLogoUrl} 
              alt="Club Logo" 
              style={{
                height: '58px',
                width: '58px',
                objectFit: 'contain',
                borderRadius: '8px',
                backgroundColor: 'rgba(255,255,255,0.1)'
              }}
            />
          )}
          <div style={{
            fontSize: '17px',
            fontWeight: '600',
            color: 'white',
            lineHeight: '1.3',
            textAlign: branding?.clubLogoUrl ? 'left' : 'center'
          }}>
            {clubInfo?.name || 'Loading...'}
          </div>
        </div>
        
        {/* User Info */}
        <div style={{
          fontSize: '13px',
          color: 'rgba(255,255,255,0.7)',
          marginBottom: '12px',
          textAlign: 'center',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {user?.email || 'Not logged in'}
        </div>
        
        {/* Role Badge */}
        <div style={{
          display: 'flex',
          justifyContent: 'center'
        }}>
          <div style={{
            fontSize: '11px',
            color: 'white',
            padding: '4px 10px',
            background: 'rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(4px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '20px',
            display: 'inline-block',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {userRole === 'admin' ? 'Administrator' : userRole === 'viewer' ? 'Viewer' : 'User'}
          </div>
        </div>
        
        {/* Subscription Info */}
        {clubInfo && (
          <div style={{
            marginTop: '16px',
            paddingTop: '16px',
            borderTop: '1px solid rgba(255,255,255,0.2)',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '11px',
              color: 'rgba(255,255,255,0.6)',
              marginBottom: '4px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Subscription
            </div>
            <div style={{
              fontSize: '13px',
              color: 'white',
              fontWeight: '600',
              marginBottom: '6px',
              textTransform: 'capitalize'
            }}>
              {clubInfo.subscriptionTier || 'Free'} Plan
            </div>
            
            {clubInfo.subscriptionStatus && (
              <div style={{
                display: 'flex',
                justifyContent: 'center'
              }}>
                <div style={{
                  fontSize: '12px',
                  color: clubInfo.subscriptionStatus === 'active' ? '#86efac' : 
                         clubInfo.subscriptionStatus === 'trial' ? '#fde047' : 
                         '#fca5a5',
                  padding: '3px 8px',
                  backgroundColor: clubInfo.subscriptionStatus === 'active' ? 'rgba(34, 197, 94, 0.2)' : 
                                   clubInfo.subscriptionStatus === 'trial' ? 'rgba(234, 179, 8, 0.2)' : 
                                   'rgba(239, 68, 68, 0.2)',
                  borderRadius: '4px',
                  display: 'inline-block',
                  fontWeight: '500'
                }}>
                  {clubInfo.subscriptionStatus === 'active' ? '✓ Active' : 
                   clubInfo.subscriptionStatus === 'trial' ? '⏱ Trial' : 
                   clubInfo.subscriptionStatus === 'canceled' ? '✕ Canceled' :
                   clubInfo.subscriptionStatus}
                </div>
              </div>
            )}
            
            {clubInfo.subscriptionStatus === 'trial' && clubInfo.trialEndsAt && (() => {
              const now = new Date();
              const trialEndsAt = clubInfo.trialEndsAt?.toDate ? 
                clubInfo.trialEndsAt.toDate() : 
                new Date(clubInfo.trialEndsAt);
              const msRemaining = trialEndsAt - now;
              const daysRemaining = Math.max(0, Math.ceil(msRemaining / (1000 * 60 * 60 * 24)));
              
              return (
                <div style={{
                  fontSize: '12px',
                  color: daysRemaining <= 3 ? '#fca5a5' : 'rgba(255,255,255,0.6)',
                  marginTop: '4px'
                }}>
                  {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining
                </div>
              );
            })()}
          </div>
        )}
        </div>
      </div>
      
      {/* Facility & Pitch Navigation - Responsive */}
      {useCompactMode ? <CompactPitchSelector /> : <FullPitchNavigation />}
      
      {/* Bottom navigation buttons */}
      <div style={{
        padding: '12px',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        backgroundColor: 'rgba(0,0,0,0.1)',
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        flexShrink: 0
      }}>
        <button
          onClick={() => {
            onNavigateToMenu();
            if (isMobile && onMobileClose) {
              onMobileClose();
            }
          }}
          style={{
            width: '100%',
            padding: '10px 12px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '8px',
            fontSize: '13px',
            color: 'rgba(255,255,255,0.9)',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.15s ease',
            textAlign: 'left'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
          }}
        >
          Home Menu
        </button>

        <button
          onClick={() => {
            onNavigateToClubMap();
            if (isMobile && onMobileClose) {
              onMobileClose();
            }
          }}
          style={{
            width: '100%',
            padding: '10px 12px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '8px',
            fontSize: '13px',
            color: 'rgba(255,255,255,0.9)',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.15s ease',
            textAlign: 'left'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
          }}
        >
          Club Map
        </button>
        
        <button
          onClick={() => {
            onNavigateToReporting();
            if (isMobile && onMobileClose) {
              onMobileClose();
            }
          }}
          style={{
            width: '100%',
            padding: '10px 12px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '8px',
            fontSize: '13px',
            color: 'rgba(255,255,255,0.9)',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.15s ease',
            textAlign: 'left'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
          }}
        >
          Reporting
        </button>
        
        <button
          onClick={() => {
            onNavigateToSettings();
            if (isMobile && onMobileClose) {
              onMobileClose();
            }
          }}
          style={{
            width: '100%',
            padding: '10px 12px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '8px',
            fontSize: '13px',
            color: 'rgba(255,255,255,0.9)',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.15s ease',
            textAlign: 'left'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
          }}
        >
          Settings
        </button>

        <button
          onClick={() => {
            onFeedback();
            if (isMobile && onMobileClose) {
              onMobileClose();
            }
          }}
          style={{
            width: '100%',
            padding: '10px 12px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '8px',
            fontSize: '13px',
            color: 'rgba(255,255,255,0.9)',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.15s ease',
            textAlign: 'left'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
          }}
        >
          Feedback
        </button>

        <button
          onClick={() => {
            onLogout();
            if (isMobile && onMobileClose) {
              onMobileClose();
            }
          }}
          style={{
            width: '100%',
            padding: '10px 12px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '8px',
            fontSize: '13px',
            color: 'rgba(255,255,255,0.9)',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.15s ease',
            textAlign: 'left'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
          }}
        >
          Logout
        </button>
      </div>
    </>
  )};

  // Desktop Sidebar
  if (!isMobile) {
    return (
      <div style={{
        width: '250px',
        flexShrink: 0,
        backgroundColor: '#243665',
        height: '100vh',
        position: 'sticky',
        top: 0,
        left: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: useCompactMode ? 'visible' : 'hidden'
      }}>
        <SidebarContent />
      </div>
    );
  }

  // Mobile Sidebar (overlay)
  return (
    <>
      {/* Backdrop */}
      {isMobileOpen && (
        <div
          onClick={onMobileClose}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 998,
            opacity: isMobileOpen ? 1 : 0,
            transition: 'opacity 0.3s ease'
          }}
        />
      )}
      
      {/* Sidebar */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '280px',
        maxWidth: '85vw',
        height: '100vh',
        backgroundColor: '#243665',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        zIndex: 999,
        transform: isMobileOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.3s ease',
        boxShadow: isMobileOpen ? '4px 0 20px rgba(0,0,0,0.3)' : 'none'
      }}>
        {/* Close button for mobile */}
        <button
          onClick={onMobileClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            width: '32px',
            height: '32px',
            borderRadius: '16px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            border: 'none',
            color: 'white',
            fontSize: '18px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10
          }}
        >
          ×
        </button>
        
        <SidebarContent />
      </div>
    </>
  );
};

export default AllocationSidebar;
